import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Helmet } from 'react-helmet'
import {
  useHistory,
  useParams,
  useLocation,
  useRouteMatch,
} from 'react-router-dom'
import { ABSpinner, Button, useErrorService } from '@abroad/components'
import LoadableCategories from './ViewCategories'
import AttachAudioResource from './AttachAudioResource'
import API from '../../utils/API'
import CONSTANTS from '../../utils/constants'
import { Row, Col, Container } from 'react-bootstrap'
import GrowthPlanHeader from './GrowthPlanHeader'
import { formatDate } from '../../utils/date'

const ViewGrowthPlan = () => {
  const [growthSessionData, setGrowthSessionData] = useState(null)
  const [reviewCall, setReviewCall] = useState(null)
  const [clientInfo, setClientInfo] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [surveyType, setsurveyType] = useState()
  const [logs, setLogs] = useState({ loading: true, data: null })
  const { clientId, sowId, planId } = useParams()
  const history = useHistory()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const { url } = useRouteMatch()
  const Error = useErrorService()
  const { isEditable } =
    location.state || queryParams.get('isEditable') === 'true' || {}

  useEffect(() => {
    const getUserInfo = async () => {
      try {
        const { data } = await API.AdminAPI.coachClient.getClientSpecificData(
          clientId,
          {
            fields: ['fullName', 'fname'],
          },
        )
        if (data) {
          setClientInfo(data)
        }
      } catch (e) {
        setClientInfo(null)
        Error.showError(e)
      }
    }
    getUserInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    setIsLoading(true)
    const getGrowthPlan = async () => {
      try {
        const { data } = await API.CoachAPI.coachingSession.getClientSession(
          planId,
          clientId,
        )
        if (data) {
          const keySkillsData = data?.keySkills?.map((keySkill) => ({
            _id: keySkill?._id,
            title: keySkill?.keySkill?.title,
            description: keySkill?.keySkill?.description,
          }))
          setGrowthSessionData({ ...data, keySkills: keySkillsData })
          setIsLoading(false)
        }
      } catch (e) {
        setIsLoading(false)
        Error.showError(e)
      }
    }
    if (planId) getGrowthPlan()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId])

  useEffect(() => {
    const getSurveyLogsAndSession = async () => {
      setLogs({ loading: true, data: null })
      try {
        // First check if survey is taken
        const { data: surveyData } =
          await API.AdminAPI.common.getClientSurveyLogs(clientId, sowId, planId)

        // If user has taken assessment (surveyType exists)
        if (surveyData?.surveyType) {
          setLogs({ loading: false, data: surveyData?.logs })
          setsurveyType(surveyData?.surveyType)

          // Extract values from logs structure if surveyLogs is not available
          let surveyAnswers = surveyData?.surveyLogs || {}

          // If surveyLogs is empty/null, extract from logs structure
          if (!surveyAnswers || Object.keys(surveyAnswers).length === 0) {
            surveyAnswers = {}
            const allLogs = [
              ...(surveyData?.logs?.awareness || []),
              ...(surveyData?.logs?.wisdom || []),
              ...(surveyData?.logs?.prosperity || []),
              ...(surveyData?.logs?.purposeAndLife || []),
            ]

            allLogs.forEach((log) => {
              if (log?.keyQuestions) {
                log.keyQuestions.forEach((question) => {
                  if (question?.name && question?.value) {
                    surveyAnswers[question.name] = question.value
                  }
                  // Also check stages
                  if (question?.stages) {
                    question.stages.forEach((stage) => {
                      if (stage?.name && stage?.value) {
                        surveyAnswers[stage.name] = stage.value
                      }
                    })
                  }
                })
              }
            })
          }

          setReviewCall(surveyAnswers)
        } else {
          // User hasn't taken assessment, get data from session
          const { data: sessionData } =
            await API.CoachAPI.coachingSession.getClientSession(
              planId,
              clientId,
            )

          // If logs exist, populate keyQuestions values with reviewCall data
          if (surveyData?.logs && sessionData?.reviewCall) {
            const updatedLogs = { ...surveyData.logs }

            // Update all log categories with reviewCall data
            Object.keys(updatedLogs).forEach((category) => {
              if (Array.isArray(updatedLogs[category])) {
                updatedLogs[category] = updatedLogs[category].map((item) => {
                  if (item?.keyQuestions) {
                    const updatedKeyQuestions = item.keyQuestions.map(
                      (question) => {
                        // If reviewCall has data for this question, use it
                        if (
                          question?.name &&
                          sessionData.reviewCall[question.name]
                        ) {
                          return {
                            ...question,
                            value: sessionData.reviewCall[question.name],
                          }
                        }
                        // Also check stages
                        if (question?.stages) {
                          const updatedStages = question.stages.map((stage) => {
                            if (
                              stage?.name &&
                              sessionData.reviewCall[stage.name]
                            ) {
                              return {
                                ...stage,
                                value: sessionData.reviewCall[stage.name],
                              }
                            }
                            return stage
                          })
                          return { ...question, stages: updatedStages }
                        }
                        return question
                      },
                    )
                    return { ...item, keyQuestions: updatedKeyQuestions }
                  }
                  return item
                })
              }
            })

            setLogs({ loading: false, data: updatedLogs })
          } else {
            setLogs({ loading: false, data: surveyData?.logs || null })
          }

          setsurveyType(null)

          // Use reviewCall data for form answers
          const surveyAnswers = sessionData?.reviewCall || {}
          setReviewCall(surveyAnswers)
        }
      } catch (e) {
        setLogs({ loading: false, data: null })
        Error.showError(e)
      }
    }
    if (sowId && planId) getSurveyLogsAndSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sowId, planId])

  if (isLoading || logs?.loading) {
    return <ABSpinner />
  }
  const getOrderedPages = (surveyType) => {
    // When user has NOT taken any assessment
    if (!surveyType) {
      return [
        { name: 'Awareness Assessment', type: 'awareness' },
        { name: 'Purpose & Life Alignment', type: 'purposeAndLife' },
        { name: 'True Prosperity Assessment', type: 'prosperity' },
      ]
    }

    if (surveyType === 'wisdom') {
      return [
        { name: 'Wisdom Assessment', type: 'wisdom' },
        { name: 'Purpose & Life Alignment', type: 'purposeAndLife' },
        { name: 'Awareness Assessment', type: 'awareness' },
      ]
    }
    if (surveyType === 'awareness') {
      const hasWisdom = logs?.data?.wisdom && logs.data.wisdom.length > 0

      const earlierAssessment = hasWisdom
        ? { name: 'Wisdom Assessment', type: 'wisdom' }
        : { name: 'TrueProsperity Assessment', type: 'prosperity' }

      return [
        { name: 'Awareness Assessment', type: 'awareness' },
        { name: 'Purpose & Life Alignment', type: 'purposeAndLife' },
        earlierAssessment,
      ]
    }
    return [
      { name: 'True Prosperity Assessment', type: 'prosperity' },
      { name: 'Purpose & Life Alignment', type: 'purposeAndLife' },
      { name: 'Awareness Assessment', type: 'awareness' },
    ]
  }

  return (
    <>
      <Helmet>
        <title>{`${
          clientInfo?.fname ? `${clientInfo?.fname}'s` : ''
        } Growth Session Plan | Abroad`}</title>
        <meta
          name='title'
          content={`${
            clientInfo?.fname ? `${clientInfo?.fname}'s` : ''
          } Growth Session Plan | Abroad`}></meta>
        <meta
          property='og:title'
          content={`${
            clientInfo?.fname ? `${clientInfo?.fname}'s` : ''
          } Growth Session Plan | Abroad`}></meta>
        <meta
          property='twitter:title'
          content={`${
            clientInfo?.fname ? `${clientInfo?.fname}'s` : ''
          } Growth Session Plan | Abroad`}></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}${url}`}></link>
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}${url}`}></meta>
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}${url}`}></meta>
      </Helmet>
      <GrowthPlanHeader
        fullName={clientInfo?.fullName}
        date={growthSessionData?.date || null}
      />
      <main>
        <>
          <Container
            className='custom-offset-lg-1 custom-col-lg-12 px-lg-0'
            fluid>
            <section className='mt-3 view-growth-plan'>
              <div className='d-flex'>
                <span className='s8 mr-3 growth-label align-self-center text-right'>
                  LOGGED BY:{' '}
                </span>
                <span className='s6'>{growthSessionData?.coach?.fullName}</span>
              </div>
              <div className='d-flex'>
                <span className='s8 mr-3 growth-label align-self-center'>
                  CALL OUTCOME:{' '}
                </span>
                <span className='s6'>
                  {growthSessionData?.callOutcome
                    ? CONSTANTS.callOutcomeObj[growthSessionData?.callOutcome]
                    : '-'}
                </span>
              </div>
              {growthSessionData?.scheduledEvent && (
                <div className='d-flex'>
                  <span className='s8 mr-3 growth-label align-self-center text-right'>
                    CALENDAR EVENT:{' '}
                  </span>
                  <span className='s6'>
                    {growthSessionData.scheduledEvent.title} -{' '}
                    {formatDate(growthSessionData.scheduledEvent.startTime)}
                  </span>
                </div>
              )}
              {isEditable && (
                <Button
                  variant='outline-dark'
                  className='btn s8 text-uppercase border-radius-3 line-height-normal mt-3 outline-dark-btn'
                  onClick={() => {
                    history.push(
                      `/coach/clients/${clientId}/growth-plan/${sowId}/${planId}`,
                    )
                  }}>
                  Edit Growth Plan
                </Button>
              )}
              <div>
                <h3 className='font-open-sans font-22 font-bold review-title view-gp-subtitle'>
                  Key Takeaways
                </h3>
                <div
                  className='s5'
                  dangerouslySetInnerHTML={{
                    __html: growthSessionData?.coachNotes,
                  }}
                />
                <Row>
                  {getOrderedPages(surveyType).map((page) => (
                    <Col
                      lg={12 / getOrderedPages(surveyType).length}
                      md={6}
                      key={page.type}>
                      <h3 className='font-open-sans font-22 font-bold review-title view-gp-subtitle mb-3'>
                        Category Notes - {page.name}
                      </h3>
                      <LoadableCategories
                        logs={logs?.data}
                        sessionLogs={{ answers: reviewCall || {} }}
                        saveLogHandler={() => {}}
                        disabled={true}
                        isViewGPSection={true}
                        type={page.type}
                      />
                    </Col>
                  ))}
                </Row>

                {growthSessionData?.postSession && (
                  <>
                    <h3 className='font-open-sans font-22 font-bold review-title view-gp-subtitle mb-3'>
                      Post Session Practice
                    </h3>
                    <div
                      className='s5'
                      dangerouslySetInnerHTML={{
                        __html: growthSessionData?.postSession,
                      }}
                    />
                  </>
                )}
                <div
                  className={classNames({
                    'view-gp-subtitle':
                      !growthSessionData?.postSession ||
                      growthSessionData?.postSession === '',
                    'my-3': growthSessionData?.postSession,
                  })}>
                  <AttachAudioResource
                    growthPlanData={growthSessionData}
                    setAudioResource={() => {}}
                    setSaveResourcePdf={() => {}}
                    disabled={true}
                  />
                </div>
              </div>
            </section>
          </Container>
        </>
      </main>
    </>
  )
}

export default ViewGrowthPlan
